import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import { filterStudiesQueryOptions } from 'app/queries/useFilterStudiesInfiniteQuery';
import useSyncStudyFromStoreWithPACSMutation from 'app/queries/useSyncStudyFromStoreWithPACSMutation';
import ApiCalls, { API } from 'app/utils/apiCalls';
import countFilteredStudiesQueryOptions from 'app/queries/countFilteredStudiesQueryOptions';
import { MutationStateFromUseMutation } from 'app/type-utils/ReactQueryInference';

export default function useSyncAllStudiesFromStoreWithPACSMutation() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useSyncStudyFromStoreWithPACSMutation();

  return useMutation({
    mutationKey: ['syncAllStudiesFromStoreWithPACS'],
    mutationFn: async ({
      abortController,
      filters,
    }: {
      abortController: AbortController;
      filters: API.StudiesFilter;
    }) => {
      const syncedStudies: string[] = [];
      let pageNumber = 1;
      while (!abortController.signal.aborted) {
        let data;
        try {
          ({ data } = await ApiCalls.filterStudies({
            ...filters,
            isPacsSynced: false,
            page_number: pageNumber, // Because they get deleted from the search results
            use_study_structure: true,
          }));
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted');
            break;
          }
          console.error('Error fetching studies:', error);
          continue;
        }
        const studies = data?.studies || [];

        if (studies.length === 0) break;

        const newStudiesToSync = _.filter(
          studies,
          (study) => !_.includes(syncedStudies, study._id)
        );
        if (newStudiesToSync.length === 0) {
          pageNumber++;
          continue;
        }
        for (const study of newStudiesToSync) {
          try {
            const result = await mutateAsync({ study });
          } catch (error: any) {
            if (error.name === 'AbortError') {
              console.log('Sync aborted');
              break;
            }
            console.error('Error syncing study with PACS:', error);
          }
          if (abortController.signal.aborted) break;

          syncedStudies.push(study._id);
        }
        queryClient.invalidateQueries({ ...filterStudiesQueryOptions(), exact: false });
        queryClient.invalidateQueries({
          ...countFilteredStudiesQueryOptions(),
          exact: false,
        });
      }
    },
    scope: { id: 'syncAllStudiesFromStoreWithPACS' },
  });
}

export const useSyncAllStudiesFromStoreWithPACSMutationState = () => {
  const mutationState = useMutationState<
    MutationStateFromUseMutation<typeof useSyncAllStudiesFromStoreWithPACSMutation>
  >({
    filters: {
      mutationKey: ['syncAllStudiesFromStoreWithPACS'],
    },
  });

  return mutationState;
};
